/**
 * Does this file have a redacted output right now?
 *
 * Two sources can answer, and they are not interchangeable:
 *
 * - A row loaded by `fetchCaseContentsQuery` carries the asset list itself, so
 *   the answer is whether that list has anything in it.
 * - A row that exists only in the request roster carries a BOOLEAN instead —
 *   the roster asks for one asset id purely to settle existence and keeps the
 *   verdict, not the asset (see `RequestRosterEntry.hasRedactedOutput`).
 *
 * Which is why this is a function rather than a field read. The roster must
 * NOT fake an asset record to look like a loaded row: several callers take
 * `redactedMediaAssets.records[0]` and use the asset — its id, its signed uri,
 * its size — and a synthesized record would flow into a download. An absent
 * list means "not loaded", never "none"; the boolean is the only honest answer
 * a roster row can give, and it answers existence and nothing else.
 *
 * Use this ONLY where existence is the question (the redaction sort, the
 * redacted / not-redacted filter, the Redacted / Original pill). Anything that
 * needs the asset itself has to wait for the loaded row, or refetch by id the
 * way the download review does.
 */

export interface RedactedOutputSource {
  /** Present once the heavy per-row query has loaded; absent on a roster row. */
  readonly redactedMediaAssets?: { records?: unknown[] | null } | null;
  /** The roster's verdict, used when the asset list has not been loaded. */
  readonly hasRedactedOutput?: boolean;
}

export const fileHasRedactedOutput = (
  file: RedactedOutputSource | null | undefined
): boolean => {
  if (!file) {
    return false;
  }
  // A loaded row answers from the assets it actually carries; `records` being
  // an empty array is a real "none", which is why presence of the LIST — not
  // of a record — is what decides which source to trust.
  if (file.redactedMediaAssets) {
    return (file.redactedMediaAssets.records?.length ?? 0) > 0;
  }
  return file.hasRedactedOutput === true;
};
